import { Components, Theme } from '@mui/material'
import { DISABLED_OPACITY } from './disabledOpacity'

declare module '@mui/material/MenuItem' {
  interface MenuItemOwnProps {
    color?:
      /** The menu's own call to action — the item that adds or creates rather than picks one of the options. */
      | 'primary'
      | 'danger'
      // @deprecated - to be removed in favor of the semantic 'danger'
      | 'red'
  }
}

export const menuContainerStyle = (theme: Theme) => ({
  minWidth: 200,
  marginTop: theme.spacing(1.5),
  boxShadow: theme.customShadows.sm,
  // Horizontal padding is on the container not the list, so it's in the right place for scrollbars
  padding: theme.spacing(0, 1),
  borderRadius: theme.shape['radius-md'],
  backgroundColor: theme.palette.bg.surface.elevated,
  backgroundImage: 'none', // MUI applies a default background image for Paper
  '& .MuiMenu-list': menuListStyle(theme),
})

export const menuListStyle = (theme: Theme) => ({
  // Vertical padding is on the list, not on the container, so that if we scroll we scroll off the top of the container
  padding: theme.spacing(1, 0),

  // Dividers within menus don't go full width. MUI only spaces a divider that is the immediate
  // sibling of a MenuItem, so it loses its vertical margin when the item before it is wrapped
  // (e.g. in the span a Tooltip needs around a disabled item).
  '& .MuiDivider-root': {
    margin: theme.spacing(1, 2),
  },

  // Menu subheaders
  '& .MuiListSubheader-root': {
    ...theme.typography['small-regular'],
    color: theme.palette.text.subtle,
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.palette.bg.surface.elevated,
    // Increase the top pad, but offset with a negative margin
    // This means that the text doesn't move up at all when sticky
    paddingTop: theme.spacing(2),
    marginTop: theme.spacing(-1),
  },
})

// Menu's own icon slot: sized here rather than on MuiListItemIcon globally, so it doesn't reach
// into other ListItemIcon consumers (e.g. the app shell sidebar) that size their own wrapper.
export const menuItemIconStyle = (theme: Theme) => ({
  minWidth: '18px',
  width: '18px',
  height: '18px',
  marginRight: theme.spacing(2),
  color: theme.palette.fg.default,
  '& > .MuiSvgIcon-root': {
    fontSize: 18,
  },
  '& > svg:not(.MuiSvgIcon-root)': {
    width: '18px',
    height: '18px',
  },
  '& > .MuiCircularProgress-root': {
    width: '18px !important', // CircularProgress sets its size inline, so specificity has to beat it
    height: '18px !important',
  },
})

export const MuiMenu: Components<Omit<Theme, 'components'>>['MuiMenu'] = {
  styleOverrides: {
    paper: ({ theme }) => menuContainerStyle(theme),
  },
}

export const MuiMenuItem: Components<Omit<Theme, 'components'>>['MuiMenuItem'] = {
  styleOverrides: {
    root: ({ theme }) => ({
      // Duplicated in MuiAutocomplete option
      borderRadius: theme.shape['radius-sm'],
      padding: theme.spacing(0.5, 2),
      minHeight: '28px',
      ...theme.typography['body-regular'],
      color: theme.palette.text.default,
      // `data-active` marks the keyboard-highlighted row in an aria-activedescendant listbox (focus stays
      // on the input, so :hover/:focus can't express it) — it gets the same fill as hover.
      '&:hover, &[data-active="true"]': {
        backgroundColor: theme.palette.bg.interactive.hover,
      },
      '&:not(:last-child)': {
        marginBottom: theme.spacing(0.5),
      },
      '&.Mui-disabled': {
        opacity: DISABLED_OPACITY,
      },

      // Consistent (smaller) icon size and spacing
      '& .MuiListItemIcon-root': menuItemIconStyle(theme),
      // An icon after the text keeps the slot but takes its gap on the side facing the text
      '& .MuiListItemText-root ~ .MuiListItemIcon-root': {
        marginRight: 0,
        marginLeft: theme.spacing(2),
      },
      // MUI gives ListItemAvatar a 56px min-width. Size it to the menu's 20px avatar so rows line up,
      // and leave the width open so a wider logo pushes the text along instead of overlapping it.
      '& .MuiListItemAvatar-root': {
        minWidth: '20px',
        marginRight: theme.spacing(2),
        display: 'flex',
        alignItems: 'center',
      },
    }),
  },
  variants: [
    {
      props: { color: 'primary' },
      style: ({ theme }) => ({
        color: theme.palette.text.brand.default,
        '& .MuiListItemIcon-root': {
          color: theme.palette.fg.brand.default,
        },
      }),
    },
    {
      // Handle the deprecated "red" color
      props: (props) => props.color === 'danger' || props.color === 'red',
      style: ({ theme }) => ({
        color: theme.palette.text.danger.default,
        '& .MuiListItemIcon-root': {
          color: theme.palette.fg.danger.default,
        },
      }),
    },
  ],
}
